import { SUPPORT_PERMISSION_BY_LINE } from '@onr/schema'
import {
  schemaDocumentSnapshotQueryOptions,
  schemaQuerySnapshotQueryOptions,
  schemaSingleDocumentSnapshotQueryOptions,
} from '@valian/react-kitchen-sink'

import { collections } from '@/schema'

export const requesterOpenSupportRequestsQuery = (regionId: string | null) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', 'requester', regionId, 'open'],
    factory: collections.supportRequests,
    snapshotOptions: { includeMetadataChanges: true },
    query: regionId
      ? {
          name: `Open support requests of region ${regionId}`,
          where: [
            ['regionId', '==', regionId],
            ['requesterStatus', 'in', ['open', 'waitingRequester']],
          ],
        }
      : null,
  })

export const requesterSettledSupportRequestsQuery = (regionId: string | null, settledSince: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', 'requester', regionId, 'settled', settledSince.getTime()],
    factory: collections.supportRequests,
    snapshotOptions: { includeMetadataChanges: true },
    query: regionId
      ? {
          name: `Recently settled support requests of region ${regionId}`,
          where: [
            ['regionId', '==', regionId],
            ['settledAt', '>=', settledSince],
          ],
        }
      : null,
  })

export const supportRequestQuery = (number: number | null) =>
  schemaDocumentSnapshotQueryOptions({
    queryKey: ['supportRequests', number],
    factory: collections.supportRequests,
    snapshotOptions: { includeMetadataChanges: true },
    id: number === null ? null : String(number),
  })

export const requesterSupportRequestItemsQuery = (number: number | null) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', number, 'items', 'requester'],
    factory: collections.supportRequests(String(number ?? 0)).items,
    snapshotOptions: { includeMetadataChanges: true },
    query:
      number === null
        ? null
        : {
            name: `Requester items of support request ${number}`,
            where: [['audience', '==', 'requester']],
          },
  })

export const AGENT_OPEN_STATUSES = ['toHandle', 'waitingRequester', 'waitingValian'] as const

export const agentOpenSupportRequestsQuery = () =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', 'agent', 'open'],
    factory: collections.supportRequests.agent.group,
    snapshotOptions: { includeMetadataChanges: true },
    query: {
      name: 'Open support requests for the agents',
      where: [['status', 'in', [...AGENT_OPEN_STATUSES]]],
    },
  })

export const agentSettledSupportRequestsQuery = (settledSince: Date) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', 'agent', 'settled', settledSince.getTime()],
    factory: collections.supportRequests.agent.group,
    snapshotOptions: { includeMetadataChanges: true },
    query: {
      name: 'Recently settled support requests for the agents',
      where: [['settledAt', '>=', settledSince]],
    },
  })

export const supportRequestAgentQuery = (number: number | null) =>
  schemaSingleDocumentSnapshotQueryOptions({
    queryKey: ['supportRequests', number, 'agent'],
    factory: collections.supportRequests(String(number ?? 0)).agent,
    snapshotOptions: { includeMetadataChanges: true },
    enabled: number !== null,
  })

export const agentSupportRequestItemsQuery = (number: number | null) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['supportRequests', number, 'items', 'agents'],
    factory: collections.supportRequests(String(number ?? 0)).items,
    snapshotOptions: { includeMetadataChanges: true },
    query: number === null ? null : { name: `Agent items of support request ${number}` },
  })

export const escouadeAgentsQuery = () =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['piloteUsers', 'escouade'],
    factory: collections.piloteUsers,
    query: {
      name: 'Escouade agents',
      where: [['permissions', 'array-contains', SUPPORT_PERMISSION_BY_LINE.l1]],
    },
  })

export const piloteUserAccountQuery = (piloteUserId: number | null) =>
  schemaDocumentSnapshotQueryOptions({
    queryKey: ['piloteUsers', piloteUserId],
    factory: collections.piloteUsers,
    id: piloteUserId === null ? null : String(piloteUserId),
  })
